import React, { useState, useEffect } from "react";
import { Create } from "@refinedev/mui";
import { useNavigate, useParams } from "react-router";
import { Box, TextField, FormControlLabel, Switch, Button, Grid, Typography, Chip } from "@mui/material";
import { AgreementProductFormValues } from "@modules/agreements/types/agreementProduct";
import { Product } from "@modules/defaultProducts/types/product";
import { getProductsService } from "@modules/defaultProducts/services";
import { createAgreementProductService } from "@modules/agreements/services/realAgreementProductService";

export const AgreementProductCreate = React.memo(() => {
  const navigate = useNavigate();
  const { agreementId } = useParams<{ agreementId: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AgreementProductFormValues>({
    name: "",
    description: "",
    active: true,
    isActive: true,
    default_product: 0,
    globalProductId: 0,
    agreement: 0,
    metadata: {},
    customMetadata: {},
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProductsService();
        setProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  const handleInputChange = (field: keyof AgreementProductFormValues, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }
    
    if (!formData.globalProductId) {
      newErrors.globalProductId = "Produto global é obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async () => {
    if (!agreementId || !validateForm()) return;
    
    setLoading(true);
    try {
      await createAgreementProductService(formData, parseInt(agreementId));
      navigate(`/agreements/edit/${agreementId}`);
    } catch (error) {
      console.error("Error creating agreement product:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedProduct = products.find(p => p.id === formData.globalProductId);

  return (
    <Create
      title="Criar Produto do Agreement"
      saveButtonProps={{ loading }}
    >
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome do Produto"
              fullWidth
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name || ""}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Produto Global"
              select
              fullWidth
              value={formData.globalProductId}
              onChange={(e) => handleInputChange("globalProductId", parseInt(e.target.value))}
              error={!!errors.globalProductId}
              helperText={errors.globalProductId || ""}
            >
              <option value="">Selecione um produto</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange("isActive", e.target.checked)}
                />
              }
              label="Produto Ativo"
            />
          </Grid>

          {selectedProduct && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Metadados do Produto Global
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {Object.values(selectedProduct.metadata || {}).map((meta, index) => (
                  <Chip
                    key={index}
                    label={`${meta.value} (${meta.type})`}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/agreements/edit/${agreementId}`)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={loading}
              >
                {loading ? "Salvando..." : "Salvar"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Create>
  );
});
